import { AriaDatapoint } from './AriaDatapoint';
import type { DatapointOptions } from 'owa-analytics-types';
import { getQueryStringParameter, hasQueryStringParameter } from 'owa-querystring';
import { getUniquePropertyString } from '../utils/getUniquePropertyString';
import { hasUserWindowHadFocusSince } from '../utils/visibilityState';
import { lazyLogPerformanceDatapoint } from '../lazyFunctions';
import { TraceErrorObject, trace } from 'owa-trace';
import { DatapointStatus } from 'owa-analytics-types';
import { GenericKeys } from '../types/GenericKeys';
import { safeRequestAnimationFrame } from 'owa-performance';
import type { PerformanceWithMemory } from 'owa-performance';
import {
    getGlobalImportStartTime,
    getLazyLoadedModulesAndCleanup,
    LazyLoadedModules,
} from 'owa-bundling-light';
import type { ErrorType } from 'owa-errors';
import { endLongRunningTaskTracking, trackLongRunningTask } from '../utils/trackLongRunningTasks';
import { isBootFeatureEnabled } from 'owa-metatags';

// Lazy Module Stats for Health Data
// d: duration in ms
// a: attempts
type LazyModuleStats = { d: number; a?: number };

// Health data
// ls: LayoutScore
// lr: Number of long running tasks
// md: Memory difference between the start and end of the datapoint
// lm: Lazy Modules loaded during the datapoint execution
type Health = { ls?: number; lr?: number; md?: number; lm?: LazyModuleStats[]; lmc?: number };

const performanceMarktQueryStringParam = 'pm';
const performanceMarkPrefix = 'owa-';
const defaultDatapointTimeout = 60 * 1000; // 60 seconds

let windowDefined = typeof window !== 'undefined';
let performanceMarkEnabled = windowDefined && window.performance && !!window.performance.mark;

let datapointLayoutScore: number = -1;
export function setDatapointLayoutScore(score: number) {
    datapointLayoutScore = score;
}

export class PerformanceDatapoint extends AriaDatapoint {
    hasEnded = false;
    private startTime: number | undefined;
    private perfStartTime: number | undefined;
    private startMemory: number | undefined = 0;
    private startScore: number;
    private timeBeforePause: number = 0;
    waterfallTimings: { [key: string]: number } | undefined;
    duration: number | undefined;
    allRequestIds: string[] = [];
    responseCorrelationVectors: string[] = [];
    madeNetworkRequest = false;
    constructor(eventName: string, options?: DatapointOptions) {
        super(eventName, undefined, options);
        const importTime = getGlobalImportStartTime();
        this.startTime = importTime || Date.now();
        this.startScore = datapointLayoutScore;
        this.performanceMark('s');

        if (importTime) {
            this.addData('BundleTime', Date.now() - importTime);
        }

        if (isBootFeatureEnabled('performanceDatapointHealth')) {
            trackLongRunningTask(eventName, this.startTime);
            this.startMemory = (self?.performance as PerformanceWithMemory)?.memory?.usedJSHeapSize;
            this.perfStartTime = self?.performance?.now();
        }

        // if the the datapoint doesn't end in 60 seconds, then end it with a timeout error
        setTimeout(
            // eslint-disable-next-line @typescript-eslint/no-implied-eval -- This is a function
            this.endWithTimeout.bind(this) as Function,
            options && typeof options.timeout == 'number'
                ? options.timeout
                : defaultDatapointTimeout
        );
    }
    isPeformanceMarkEnabled(): boolean {
        if (!performanceMarkEnabled) {
            return false;
        }

        return (
            hasQueryStringParameter(performanceMarktQueryStringParam) &&
            (!hasQueryStringParameter(performanceMarktQueryStringParam) ||
                this.eventName
                    .toLowerCase()
                    .indexOf(
                        getQueryStringParameter(performanceMarktQueryStringParam).toLowerCase()
                    ) > -1)
        );
    }

    addCheckmark(checkmarkName: string): number {
        this.performanceMark(checkmarkName);
        const time = this.timeFromStart();
        this.addToWaterfall(checkmarkName, time);
        return time;
    }
    addCheckpoint(checkpoint: string) {
        this.addToWaterfall(checkpoint, 1);
    }
    private addToWaterfall(key: string, time: number) {
        if (!this.waterfallTimings) {
            this.waterfallTimings = {};
        }
        try {
            const uniqueKey = getUniquePropertyString(this.waterfallTimings, key);
            if (uniqueKey) {
                this.waterfallTimings[uniqueKey] = time;
            }
        } catch {
            // We add checkmarks automatically within the action framework and there can be a lot
            // with the same exact name. So if we can't find a unique property name, don't add the
            // checkmark
        }
    }
    endWithError(
        status: DatapointStatus | ((duration?: number) => DatapointStatus),
        error?: TraceErrorObject,
        duration?: number,
        errorType?: ErrorType
    ): void {
        this.end(duration, status, error, errorType);
    }
    endAfterAnimationFrame() {
        safeRequestAnimationFrame(() => {
            this.end();
        });
    }
    end(
        duration?: number,
        overrideStatus?: DatapointStatus | ((duration?: number) => DatapointStatus),
        error?: TraceErrorObject,
        errorType?: ErrorType
    ): void {
        if (!this.hasEnded) {
            const totalDuration =
                typeof duration === 'number'
                    ? Math.floor(duration)
                    : this.timeFromStart() + this.timeBeforePause;

            this.performanceMark('e');
            // if duration is not passed in, then calculate the time based of the current time it was ended and the time the datapoint was created.
            this.addData(GenericKeys.e2eTimeElapsed, totalDuration);

            if (isBootFeatureEnabled('performanceDatapointHealth')) {
                this.addHealth(totalDuration);
            }

            this.hasEnded = true;
            lazyLogPerformanceDatapoint.importAndExecute(this, overrideStatus, errorType, error);

            if (error) {
                trace.warn(`Datapoint ${this.eventName} ended with an error of ${error.message}`);
            }
        }
    }
    markUserPerceivedTime(waitForAnimationFrame?: boolean) {
        if (waitForAnimationFrame) {
            safeRequestAnimationFrame(this.addUserPerceivedTime.bind(this));
        } else {
            this.addUserPerceivedTime();
        }
    }
    invalidate() {
        this.hasEnded = true;
    }
    pause() {
        this.timeBeforePause += this.timeFromStart();
        this.startTime = undefined;
    }
    resume() {
        if (!this.startTime) {
            this.startTime = Date.now();
        }
    }
    endAction(overrideStatus?: DatapointStatus, error?: TraceErrorObject): void {
        this.addData(GenericKeys.requestIds, this.allRequestIds.join(';'));
        this.addData(GenericKeys.correlationVectors, this.responseCorrelationVectors.join(';'));
        this.addData(GenericKeys.cache, this.madeNetworkRequest ? 'Cache' : 'NoCache');
        this.end(undefined, overrideStatus, error);
    }
    private addHealth(totalDuration: number): void {
        const health: Health = {};
        const endTime: number | undefined = self?.performance?.now();

        if (this.startScore > -1) {
            health.ls = datapointLayoutScore - this.startScore;
        }

        if (this.startTime) {
            const longRunningTaskCount = endLongRunningTaskTracking(
                this.eventName,
                this.startTime,
                this.startTime + totalDuration
            );
            if (longRunningTaskCount > 0) {
                health.lr = longRunningTaskCount;
            }
        }

        if (this.startMemory) {
            const current = (self?.performance as PerformanceWithMemory)?.memory?.usedJSHeapSize;
            const difference = Math.floor((current - this.startMemory) / 1024);
            if (difference > 0) {
                health.md = difference; // report in KB
            }
        }

        const lazyLoadedModules = getLazyLoadedModulesAndCleanup(this.perfStartTime);
        if (lazyLoadedModules.length > 0) {
            const lazyModules = this.matchLazyLoadedModules(lazyLoadedModules, endTime);
            if (lazyModules.length > 0) {
                health.lm = lazyModules;
            }
        }

        if (Object.keys(health).length > 0) {
            this.addData('Health', JSON.stringify(health));
        }
    }
    private matchLazyLoadedModules(
        lazyLoadedModules: LazyLoadedModules[],
        end: number
    ): LazyModuleStats[] {
        const match: LazyModuleStats[] = [];

        for (const { start, duration, attemps } of lazyLoadedModules) {
            if (this.perfStartTime && start >= this.perfStartTime && start + duration <= end) {
                let moduleStats: LazyModuleStats = {
                    d: Math.floor(duration),
                };
                if (attemps > 1) {
                    moduleStats.a = attemps;
                }
                match.push(moduleStats);
            }
        }

        return match;
    }
    private addUserPerceivedTime(): void {
        this.addData('UserPerceivedTime', this.timeFromStart());
    }
    private performanceMark(value: string) {
        if (this.isPeformanceMarkEnabled()) {
            window.performance.mark(performanceMarkPrefix + this.eventName + '_' + value);
        }
    }
    private endWithTimeout() {
        if (!this.hasEnded && this.startTime && hasUserWindowHadFocusSince(this.startTime)) {
            this.options = this.options || {};
            this.options.logVerbose = true;
            this.endWithError(DatapointStatus.Timeout);
        }
    }
    private timeFromStart(): number {
        return this.startTime ? Date.now() - this.startTime : 0;
    }
}
